import {
  ChevronRight,
  FileText,
  Globe2,
  Mail,
  MapPin,
  ShieldCheck,
  UserRound,
  X,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { CrmDataset } from '../../types/crm'
import type { DashboardReviewQueue } from './DashboardActionQueueModal'
import { getMissingInformationFields } from './missingInformation'

type TaskDefinition = {
  queue: DashboardReviewQueue
  label: string
  description: string
  count: number
  icon: typeof UserRound
  tone: 'critical' | 'high' | 'medium'
}

export function ReviewMissingInformationModal({
  dataset,
  onClose,
  onSelectQueue,
}: {
  dataset: CrmDataset
  onClose: () => void
  onSelectQueue?: (queue: DashboardReviewQueue) => void
  onUpdated?: () => void
}) {
  const [isClosing, setIsClosing] = useState(false)

  const modalRef = useRef<HTMLElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const closingRef = useRef(false)

  const tasks = useMemo<TaskDefinition[]>(() => {
    const analyzed = dataset.leads.map((lead) => {
      const fields = getMissingInformationFields(lead)
      const keys = new Set(fields.map((field) => field.key))

      return {
        missingOwner: !lead.ownerIdentified,
        missingContact:
          keys.has('email') &&
          keys.has('phone'),
        missingWebsite:
          keys.has('website'),
        missingLocation:
          keys.has('city') ||
          keys.has('state'),
        missingNotes:
          keys.has('notes'),
      }
    })

    return ([
      {
        queue: 'missing-owner',
        label: 'Missing Owners',
        description: 'Find the decision-maker for these companies.',
        count: analyzed.filter((item) => item.missingOwner).length,
        icon: UserRound,
        tone: 'critical',
      },
      {
        queue: 'missing-contact',
        label: 'Missing Contact Info',
        description: 'Add missing email and phone information.',
        count: analyzed.filter((item) => item.missingContact).length,
        icon: Mail,
        tone: 'high',
      },
      {
        queue: 'missing-website',
        label: 'Missing Website',
        description: 'Add the company website.',
        count: analyzed.filter((item) => item.missingWebsite).length,
        icon: Globe2,
        tone: 'medium',
      },
      {
        queue: 'missing-location',
        label: 'Missing Location',
        description: 'Complete the city or state.',
        count: analyzed.filter((item) => item.missingLocation).length,
        icon: MapPin,
        tone: 'medium',
      },
      {
        queue: 'missing-notes',
        label: 'Missing Notes',
        description: 'Add useful CRM notes.',
        count: analyzed.filter((item) => item.missingNotes).length,
        icon: FileText,
        tone: 'medium',
      },
    ] as TaskDefinition[]).filter((task) => task.count > 0)
  }, [dataset.leads])

  const beginClose = useCallback(() => {
    if (closingRef.current) return

    closingRef.current = true
    setIsClosing(true)

    const reducedMotion =
      window.matchMedia?.(
        '(prefers-reduced-motion: reduce)',
      ).matches ?? false

    closeTimerRef.current = window.setTimeout(
      onClose,
      reducedMotion ? 0 : 180,
    )
  }, [onClose])

  useEffect(() => {
    const appShell =
      document.querySelector<HTMLElement>('.app-shell')

    const previousOverflow =
      document.body.style.overflow

    appShell?.classList.add('is-lead-modal-open')
    document.body.style.overflow = 'hidden'

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        beginClose()
        return
      }

      if (
        event.key !== 'Tab' ||
        !modalRef.current
      ) {
        return
      }

      const focusable = Array.from(
        modalRef.current.querySelectorAll<HTMLElement>(
          'button:not(:disabled), [href]',
        ),
      )

      if (focusable.length === 0) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault()
        last.focus()
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      appShell?.classList.remove('is-lead-modal-open')
      document.body.style.overflow = previousOverflow

      window.removeEventListener(
        'keydown',
        handleKeyDown,
      )

      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current)
      }
    }
  }, [beginClose])

  return createPortal(
    <div
      className={`lead-modal-layer${
        isClosing ? ' is-closing' : ''
      }`}
      data-testid="review-task-selector"
    >
      <button
        aria-label="Close missing information review"
        className="lead-modal-backdrop"
        onClick={beginClose}
        type="button"
      />

      <section
        aria-labelledby="review-missing-title"
        aria-modal="true"
        className="lead-modal review-modal review-task-modal"
        ref={modalRef}
        role="dialog"
      >
        <header className="review-modal-header">
          <div className="review-modal-heading">
            <span>Quick action</span>

            <h2 id="review-missing-title">
              Review Missing Information
            </h2>

            <p>
              What do you want to work on,{' '}
              <span className="attention-name">
                William
              </span>
              ?
            </p>
          </div>

          <button
            aria-label="Close missing information review"
            className="icon-button lead-modal-close"
            onClick={beginClose}
            title="Close"
            type="button"
          >
            <X size={21} />
          </button>
        </header>

        <div className="lead-modal-content review-task-content">
          {tasks.length > 0 ? (
            <div
              aria-label="Missing information tasks"
              className="review-task-list"
              role="list"
            >
              {tasks.map((task) => {
                const Icon = task.icon

                return (
                  <button
                    className={`review-task-row is-${task.tone}`}
                    key={task.queue}
                    onClick={() =>
                      onSelectQueue?.(task.queue)
                    }
                    role="listitem"
                    type="button"
                  >


                    <div className="review-task-icon">
                      <Icon
                        aria-hidden="true"
                        size={19}
                      />
                    </div>

                    <div className="review-task-copy">
                      <strong><b className="review-task-inline-count">{task.count}</b> {task.label}</strong>
                      <span>
                        {task.description}
                      </span>
                    </div>

                    <ChevronRight
                      aria-hidden="true"
                      className="review-task-chevron"
                      size={20}
                    />
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="review-task-complete">
              <strong>
                Everything is complete.
              </strong>

              <span>
                There are no missing-information
                tasks in the current CRM.
              </span>
            </div>
          )}
        </div>

        <footer className="lead-modal-footer review-modal-footer">
          <ShieldCheck size={14} />
          Choose a task to review only that type
          of missing information.
        </footer>
      </section>
    </div>,
    document.body,
  )
}





